import { Input } from 'antd';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import { FiCamera, FiImage, FiRepeat } from 'react-icons/fi';
import Webcam from 'react-webcam';
import { GrGallery } from 'react-icons/gr';

import classes from './camera.module.scss';
import { blobToBase64, compressBase64Image } from '@/utils/services/images';
import { useStore } from '@/store/zustand-solicitud';
import { loadCameras, startCamera } from '@/utils/services/camara';
import solicitud from '@/models/solicitud';

type Camera = {
  deviceId: string;
  facingMode?: string | string[];
  focusDistance?: { min: number; max: number };
  focusMode?: string;
  frameRate?: { min: number; max: number };
  selector?: string;
};

type CameraComponentProps = {
  type?: 'photo';
  position?: 'front' | 'back';
  overlay?: 'card' | 'face';
  upload?: boolean;
  galeria?: boolean;
  onClose: () => void;
  onCapture: (image: string, capturedFromCamera: boolean) => void;
};

const CameraComponent: React.FC<CameraComponentProps> = (props) => {
  const {
    position = 'back',
    overlay,
    upload,
    galeria = false,
    onClose,
    onCapture,
  } = props;

  const cameraType = useStore((state) => state.cameraType);
  const isMobile = useStore((state) => state.isMobile);
  const [overlayImage, setOverlayImage] = useState<string | null>(null);

  const DEBUG_MODE = false;
  const webcamRef = useRef<Webcam>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const [cameras, setCameras] = useState<Camera[]>([]);
  const [currentCamera, setCurrentCamera] = useState<Camera | undefined>();
  const [colorPrimary, setColorPrimary] = useState<string | null>(null);
  const [colorText, setColorText] = useState<string | null>(null);

  const [available, setAvailable] = useState(false);
  const [loading, setLoading] = useState(false);

  const [permissionsGranted, setPermissionsGranted] = useState(false);

  const [contraints, setContraints] = useState<
    MediaTrackConstraints | undefined
  >();

  const setScreen = useStore((state) => state.setScreen);
  useEffect(() => {
    if (cameraType === 'back') {
      setOverlayImage('/assets/id-back-vertical.png');
    } else {
      setOverlayImage('/assets/id-front-vertical.png');
    }
  }, [cameraType]);

  useEffect(() => {
    const requestPermissionsAndSetup = async () => {
      try {
        // Solicitar permisos de la cámara
        await startCamera();
        setPermissionsGranted(true); // Permisos otorgados
      } catch (error) {
        console.error('No se pudo acceder a la cámara:', error);
      }
    };

    requestPermissionsAndSetup();
  }, []);

  useEffect(() => {
    if (!permissionsGranted) return; // Esperar hasta que los permisos sean otorgados
  
    const setup = async () => {
      try {
        let nCameras = await loadCameras();
        if (isMobile) {
          if (cameraType !== 'selfie') {
            // Para documentos: solo cámara trasera
            nCameras = nCameras.filter((x) =>
              x.facingMode?.includes('environment')
            );
          }
          // Para selfie: mostrar todas las cámaras para permitir cambiar entre ellas
        }
        setCameras(nCameras);
      } catch (error) {
        console.error('Error cargando cámaras:', error);
      }
    };
  
    setup();
  }, [permissionsGranted, isMobile, cameraType]); // Añadir cameraType como dependencia

  useEffect(() => {
    if (cameras.length === 0) {
      return;
    }
    setupCamera();
  }, [cameras]);

  useEffect(() => {
    const timeout = setTimeout(() => setAvailable(true), 1000);
    return () => clearTimeout(timeout);
  }, []);

  const setupCamera = (camera?: Camera) => {
    try {
      camera = selectCamera(camera);
      setCurrentCamera(camera);

      const nContraints: MediaTrackConstraints = {
        deviceId: camera?.deviceId ?? '',
      };

      if (isMobile) {
        const cameraFacing = Array.isArray(camera?.facingMode)
          ? camera?.facingMode[0]
          : camera?.facingMode;
        nContraints.facingMode = cameraFacing || (position === 'front' ? 'user' : 'environment');
        nContraints.width = { ideal: 1280 };
        nContraints.height = { ideal: 720 };
      }

      setContraints(nContraints);
    } catch (error) {
      if (DEBUG_MODE) {
        console.log(error);
      }
      console.error(error);
    }
  };

  const selectCamera = (camera?: Camera): Camera => {
    try {
      if (camera) {
        camera.selector = `Cambio de camara (${cameras.length} camaras)`;
        return camera;
      }
  
      if (!isMobile) {
        cameras[0].selector = `Version web (${cameras.length} camaras)`;
        return cameras[0];
      }
  
      if (cameras.length === 1) {
        cameras[0].selector = 'Camara unica';
        return cameras[0];
      }
  
      if (cameraType === 'selfie' || position === 'front') {
        const frontCamera = cameras.find((x) =>
          x.facingMode?.includes('user')
        );
        if (frontCamera) {
          frontCamera.selector = `Camara frontal (${cameras.length} camaras)`;
          return frontCamera;
        }
      }
  
      // Seleccionar la cámara trasera con el mejor focus distance
      const focusDistance = cameras.sort(
        (a, b) => (b.focusDistance?.min ?? 0) - (a.focusDistance?.min ?? 0)
      );
  
      const focusMode = focusDistance.filter(
        (x) => x.focusMode && x.focusMode.includes('continuous')
      );
  
      if (focusMode.length > 0) {
        focusMode[0].selector = `Camara trasera con focus mode continuous (${focusMode.length} camaras)`;
        return focusMode[0];
      }
  
      focusDistance[0].selector = `Camara trasera con focus distance mayor (${focusDistance.length} camaras)`;
      return focusDistance[0];
    } catch (error) {
      if (DEBUG_MODE) {
        console.log(error);
      }
      console.error(error);
      throw error;
    }
  };

  const onChangeCamera = () => {
    const index = currentCamera ? cameras.indexOf(currentCamera) : -1;
    const nCamera = cameras[index + 1] || cameras[0];
    setupCamera(nCamera);
  };

  const onClickShoot = () => {
    handleTakePhoto();
  };

  const handleTakePhoto = async () => {
    const imageSrc = webcamRef.current?.getScreenshot();
    if (imageSrc) {
      try {
        const compressedImage = await compressBase64Image(imageSrc, 0.9); // Compresión al 90% de calidad
        onCapture(compressedImage, true);
      } catch (error) {
        console.error('Error al comprimir la imagen:', error);
      }
      onClose();
    }
  };
  const onClickUpload = () => {
    fileRef.current?.click();
  };
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    setScreen(solicitud.screens.documento);

    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      if (reader.result) {
        onCapture(reader.result as string, false);
        setScreen(solicitud.screens.documento);
      }
    };
    reader.readAsDataURL(file);
    onClose();
  };

  const renderUpload = () => {
    if (!galeria || cameraType === "selfie") {
      return (
        <div className={classes.action}>
          <div className={classes.placeholder}></div>
        </div>
      );
    }

    // En mobile la captura de documento se hace con el teléfono en horizontal, así que la
    // botonera queda girada. Los iconos se contrarrotan para que se lean derechos.
    const iconRotation = isMobile && cameraType !== 'selfie' ? 'rotate(90deg)' : undefined;

    return (
      <div className={classes.action}>
        <div
          className={classes.upload}
          onClick={onClickUpload}
          style={{
            color: colorText || '',
            backgroundColor: colorPrimary || '',
            boxShadow: `0 0 10px ${colorPrimary || ''}`,
            transform: iconRotation,
          }}
        >
          <GrGallery />
          <Input type="file" accept="image/*" onChange={handleImageUpload} />
        </div>
        <div className={classes.label}>Galeria</div>
      </div>
    );
  };

  const renderAction = () => {
    const text = 'Tomar foto';
    const iconRotation = isMobile && cameraType !== 'selfie' ? 'rotate(90deg)' : undefined;

    return (
      <div className={classes.action}>
        <div
          className={classes.shoot}
          onClick={() => onClickShoot()}
          style={{
            color: colorText || '',
            backgroundColor: colorPrimary || '',
            boxShadow: `0 0 10px ${colorPrimary || ''}`,
            transform: iconRotation,
          }}
        >
          <div className={classes.icon}>
            <FiCamera />
          </div>
        </div>
        <div className={classes.label}>{text}</div>
      </div>
    );
  };

  if (!currentCamera || !contraints) {
    return null;
  }

  return (
    <div className={`${classes.camera} ${cameraType === 'selfie' ? classes.selfie : ''}`}>
      {/* Solo visible en mobile y en orientación horizontal; lo controla el SCSS. */}
      <div className={classes.rotateHint}>
        <div className={classes.rotateIcon}>⟳</div>
        <p>Rotá el teléfono a modo vertical para sacar la foto</p>
      </div>
      {contraints && (
        <Webcam
          className={classes.webcam}
          ref={webcamRef}
          videoConstraints={contraints}
          audio={false}
          forceScreenshotSourceSize
          screenshotFormat="image/jpeg"
          screenshotQuality={1}
        />
      )}

      {DEBUG_MODE && (
        <div className={classes.debug}>
          <div>
            <span className={classes.value}>DeviceId: </span>
            {currentCamera?.deviceId ?? '-'}
          </div>
          <div>
            <span className={classes.value}>FocusDistance: </span>
            {currentCamera?.focusDistance?.min ?? '-'}
          </div>
          <div>
            <span className={classes.value}>FocusMode: </span>
            {currentCamera?.focusMode ?? '-'}
          </div>
          <div>
            <span className={classes.value}>FrameRate: </span>
            {currentCamera?.frameRate?.max ?? '-'}
          </div>
          <div>
            <span className={classes.value}>Selector: </span>
            {currentCamera?.selector ?? '-'}
          </div>
        </div>
      )}

      <div className={classes.overlay }>
        {available && (
          <div className={classes.card}>
            {cameraType !== "selfie" && isMobile && overlayImage && (
              <div className={classes.overlay }>
                <Image
                  src={overlayImage}
                  alt="Overlay"
                  layout="fill"
                  objectFit="contain"
                />
              </div>
            )}
          </div>
        )}
        {cameraType === "selfie" &&  <div className={classes.ovalOverlay}></div>}
        
        {available && overlay === 'face' && (
          <div className={classes.face}></div>
        )}

        {available && !loading && (
          <div className={classes.bottomSection}>
            <div className={classes.actions}>
              {renderUpload()}
              {renderAction()}
            {(!cameras || cameras.length === 1) && (
              <div className={classes.action}>
                <div className={classes.placeholder}></div>
              </div>
            )}
            {cameras && cameras.length > 1 && (
              <div className={classes.action}>
                  {/* El Tooltip tenía `open` fijo, así que quedaba desplegado de forma
                      permanente sobre la vista de la cámara. El botón ya tiene su label
                      debajo ("Cambiar cámara"), así que el tooltip no aportaba nada. */}
                  <div
                    className={classes.change}
                    onClick={onChangeCamera}
                    style={{
                      color: colorText || '',
                      backgroundColor: colorPrimary || '',
                      boxShadow: `0 0 10px ${colorPrimary || ''}`,
                    }}
                  >
                    <FiRepeat />
                  </div>
                <div className={classes.label}>Cambiar cámara</div>
              </div>
            )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CameraComponent;
